import React, { useState, useEffect } from 'react';
import ProgressCounter from '../menu/ProgressCounter';
import NavigationButtons from '../menu/NavigationButtons';
import '../styles/MatchPairsGame.css';

const MatchPairsGame = ({ gameData, onComplete, isLastGame, onSubmitFinal, previousAnswers = null, currentGameIndex = 0, totalGames = 1 }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [pairs, setPairs] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [userMatches, setUserMatches] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const exercises = gameData.matchpairs || [];
  const currentExercise = exercises[currentExerciseIndex] || null;
  
  // Chiave unica per il localStorage basata sul chatId
  const getLocalStorageKey = () => {
    if (!gameData || !gameData.chatId) {
      console.error('MatchPairsGame: gameData o chatId mancante per localStorage key');
      return null;
    }
    
    return `matchpairs_partial_answers_${gameData.chatId}`;
  };
  
  // Inizializza l'esercizio corrente con le coppie
  useEffect(() => {
    if (currentExercise && currentExercise.pairs) {
      // Prepara le coppie per il display
      const leftItems = currentExercise.pairs.map(pair => ({
        id: `left-${pair.left}`,
        content: pair.left,
        type: 'left',
        originalMatch: `right-${pair.right}`,
        matched: false,
        matchedWith: null
      }));
      
      // Randomizza l'ordine delle opzioni di destra
      const rightItems = currentExercise.pairs.map(pair => ({
        id: `right-${pair.right}`,
        content: pair.right,
        type: 'right',
        originalMatch: `left-${pair.left}`, 
        matched: false,
        matchedWith: null
      })).sort(() => Math.random() - 0.5);
      
      setPairs([...leftItems, ...rightItems]);
      setSelectedLeft(null);
      
      // Ripristina gli abbinamenti precedenti, se presenti
      const exerciseId = currentExercise.id;
      const savedMatches = answers[exerciseId] || [];
      
      if (savedMatches.length > 0) {
        // Ricostruiamo gli abbinamenti utente dallo stato salvato
        const rebuiltMatches = [];
        
        savedMatches.forEach(match => {
          const leftId = `left-${match.left}`;
          const rightId = `right-${match.right}`;
          rebuiltMatches.push({ leftId, rightId });
        });
        
        setUserMatches(rebuiltMatches);
      } else {
        setUserMatches([]);
      }
    }
  }, [currentExercise, answers]);
  
  // Carica lo stato iniziale al montaggio del componente
  useEffect(() => {
    loadAnswersFromStorage();
  }, []);
  
  // Ricarica le risposte quando cambia il chatId
  useEffect(() => {
    loadAnswersFromStorage();
    setCurrentExerciseIndex(0);
    setIsSubmitting(false);
  }, [gameData.chatId, previousAnswers]);
  
  // Salva le risposte in localStorage ogni volta che vengono aggiornate
  useEffect(() => {
    if (!gameData || !gameData.chatId) {
      return;
    }
    
    const lsKey = getLocalStorageKey();
    if (lsKey && Object.keys(answers).length > 0) {
      try {
        localStorage.setItem(lsKey, JSON.stringify(answers));
      } catch (e) {
        console.error('MatchPairsGame: Errore nel salvataggio delle risposte in localStorage:', e);
      }
    }
  }, [answers, gameData]);
  
  // Funzione unificata per caricare risposte dal localStorage
  const loadAnswersFromStorage = () => {
    if (!gameData || !gameData.chatId) {
      return;
    }
    
    // Chiave per il localStorage
    const lsKey = getLocalStorageKey();
    let loadedAnswers = {};
    
    // Priorità 1: Controlla se ci sono risposte parziali salvate in localStorage
    if (lsKey) {
      try {
        const savedAnswers = localStorage.getItem(lsKey);
        
        if (savedAnswers) {
          try {
            const parsedAnswers = JSON.parse(savedAnswers);
            loadedAnswers = parsedAnswers;
          } catch (e) {
            console.error('MatchPairsGame: Errore nel parsing JSON delle risposte:', e);
          }
        }
      } catch (e) {
        console.error('MatchPairsGame: Errore nell\'accesso al localStorage:', e);
      }
    }
    
    // Priorità 2: Se non ci sono risposte in localStorage ma ci sono risposte precedenti
    if (Object.keys(loadedAnswers).length === 0 && previousAnswers && previousAnswers.results) {
      previousAnswers.results.forEach(result => {
        if (result.userMatches) {
          loadedAnswers[result.id] = result.userMatches;
        }
      });
    }
    
    // Imposta le risposte caricate nello stato
    if (Object.keys(loadedAnswers).length > 0) {
      setAnswers(loadedAnswers);
      
      // Controlla se l'utente ha già risposto all'esercizio corrente
      if (exercises && exercises.length > 0) {
        let lastAnsweredIndex = -1;
        
        // Trova l'ultima domanda risposta
        for (let i = 0; i < exercises.length; i++) {
          if (loadedAnswers[exercises[i]?.id]) {
            lastAnsweredIndex = i;
          } else {
            break;
          }
        }
        
        // Se abbiamo trovato almeno una risposta
        if (lastAnsweredIndex >= 0) {
          // Se non tutte le domande sono state risposte, vai alla prima senza risposta
          if (lastAnsweredIndex < exercises.length - 1) {
            setCurrentExerciseIndex(lastAnsweredIndex + 1);
          } else {
            // Se tutte sono state risposte, vai all'ultima
            setCurrentExerciseIndex(exercises.length - 1);
          }
        } else {
          // Se non ci sono risposte, inizia dalla prima domanda
          setCurrentExerciseIndex(0);
        }
      }
    } else {
      setCurrentExerciseIndex(0);
    }
  };
  
  // Trova il contenuto dell'elemento in base all'ID
  const getItemContentById = (itemId) => {
    const item = pairs.find(item => item.id === itemId);
    return item ? item.content : '';
  };
  
  // Gestisce il click su un elemento
  const handleItemClick = (item) => {
    // Controlla se l'elemento è già abbinato
    const isAlreadyMatched = userMatches.some(match => 
      match.leftId === item.id || match.rightId === item.id
    );
    
    if (isAlreadyMatched) {
      // Se l'elemento è già abbinato, verifichiamo se siamo in modalità eliminazione
      // e lasciamo che la funzione handleRemoveMatch gestisca questo caso
      return;
    }
    
    if (item.type === 'left') {
      // Se selezioniamo un elemento a sinistra
      setSelectedLeft(item);
    } else if (item.type === 'right' && selectedLeft) {
      // Se selezioniamo un elemento a destra e abbiamo già selezionato un elemento a sinistra
      // Creiamo un nuovo abbinamento senza verificare se è corretto
      const newMatch = {
        leftId: selectedLeft.id,
        rightId: item.id
      };
      
      setUserMatches(prev => [...prev, newMatch]);
      
      // Salva l'abbinamento nelle risposte
      const exerciseId = currentExercise.id;
      const leftContent = selectedLeft.content;
      const rightContent = item.content;
      
      // Aggiorna le risposte con il nuovo abbinamento
      setAnswers(prev => {
        const updatedAnswers = { ...prev };
        
        if (!updatedAnswers[exerciseId]) {
          updatedAnswers[exerciseId] = [];
        }
        
        // Aggiungi il nuovo abbinamento se non esiste già
        const match = { left: leftContent, right: rightContent };
        const matchExists = updatedAnswers[exerciseId].some(
          m => m.left === match.left && m.right === match.right
        );
        
        if (!matchExists) {
          updatedAnswers[exerciseId] = [...updatedAnswers[exerciseId], match];
        }
        
        return updatedAnswers;
      });
      
      // Deseleziona l'elemento a sinistra
      setSelectedLeft(null);
    }
  };
  
  // Rimuove un abbinamento
  const handleRemoveMatch = (itemId, event) => {
    // Previeni la propagazione dell'evento al genitore
    if (event) {
      event.stopPropagation();
    }
    
    const match = userMatches.find(match => 
      match.leftId === itemId || match.rightId === itemId
    );
    
    if (!match) return;
    
    // Rimuovi l'abbinamento dallo stato
    setUserMatches(prev => prev.filter(m => 
      !(m.leftId === match.leftId && m.rightId === match.rightId)
    ));
    
    // Rimuovi l'abbinamento dalle risposte
    const exerciseId = currentExercise.id;
    
    setAnswers(prev => {
      const updatedAnswers = { ...prev };
      
      if (updatedAnswers[exerciseId]) {
        // Trova i contenuti degli elementi abbinati
        const leftContent = getItemContentById(match.leftId);
        const rightContent = getItemContentById(match.rightId);
        
        // Rimuovi l'abbinamento dalle risposte
        updatedAnswers[exerciseId] = updatedAnswers[exerciseId].filter(
          m => !(m.left === leftContent && m.right === rightContent)
        );
      }
      
      return updatedAnswers;
    });
  };
  
  // Funzione per passare all'esercizio successivo
  const handleNextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(prevIndex => prevIndex + 1);
    } else {
      handleSubmit();
    }
  };

  // Funzione per tornare all'esercizio precedente
  const handlePrevExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(prevIndex => prevIndex - 1);
    }
  };
  
  // Funzione per la consegna finale
  const handleSubmitFinal = () => {
    setIsSubmitting(true);
    
    // Calcola i risultati
    const result = calculateResults();
    
    // Pulisci sempre il localStorage indipendentemente da eventuali errori
    const lsKey = getLocalStorageKey();
    if (lsKey) {
      try {
        localStorage.removeItem(lsKey);
        console.log('MatchPairsGame: Dati rimossi da localStorage durante handleSubmitFinal', lsKey);
      } catch (e) {
        console.error('MatchPairsGame: Errore nella rimozione dei dati da localStorage:', e);
      }
      
      // Aggiungiamo una pulizia di backup con un delay per maggiore sicurezza
      setTimeout(() => {
        try {
          localStorage.removeItem(lsKey);
          console.log('MatchPairsGame: Tentativo di pulizia ritardata', lsKey);
        } catch (e) {
          console.error('MatchPairsGame: Errore nella pulizia ritardata:', e);
        }
      }, 1000);
    }
    
    // Invia i risultati
    if (onComplete) {
      onComplete(result);
    }
    
    // Vai al riepilogo finale
    if (onSubmitFinal) {
      onSubmitFinal();
    }
  };
  
  // Calcola i risultati
  const calculateResults = () => {
    if (!exercises || exercises.length === 0) {
      return {
        type: 'matchpairs',
        chatId: gameData.chatId,
        score: 0,
        correctMatches: 0,
        totalMatches: 0,
        results: [],
        pairResults: [],
        completed: true,
        completedAt: new Date().toISOString()
      };
    }
    
    let totalScore = 0;
    let totalCorrect = 0;
    let totalPossible = 0;
    let exerciseResults = [];
    let allPairResults = [];
    
    exercises.forEach(exercise => {
      const userMatches = answers[exercise.id] || [];
      const correctMatches = exercise.pairs || [];
      
      let exerciseCorrect = 0;
      const exercisePossible = correctMatches.length;
      let exercisePairResults = [];
      
      // 1. Esaminiamo prima gli abbinamenti che l'utente ha fatto
      userMatches.forEach(match => {
        // Verifica se questo abbinamento è corretto
        const isCorrect = correctMatches.some(
          correctMatch => 
          correctMatch.left === match.left && 
          correctMatch.right === match.right
        );
        
        if (isCorrect) {
          exerciseCorrect++;
        }
        
        // Aggiungi questo abbinamento ai risultati dettagliati
        exercisePairResults.push({
          leftItem: match.left,
          rightItem: match.right,
          isCorrect: isCorrect,
          exerciseId: exercise.id,
          topic: exercise.topic,
          // Aggiungiamo la rappresentazione testuale dell'associazione fatta dall'utente
          userMatch: `${match.left} → ${match.right}`,
          // Per le associazioni corrette, aggiungiamo anche l'associazione corretta (identica)
          correctMatch: isCorrect ? `${match.left} → ${match.right}` : undefined
        });
      });
      
      // 2. Ora troviamo le coppie corrette che l'utente non ha abbinato (quelle mancanti)
      correctMatches.forEach(correctMatch => {
        const hasBeenMatched = userMatches.some(
          userMatch => 
          userMatch.left === correctMatch.left && 
          userMatch.right === correctMatch.right
        );
        
        if (!hasBeenMatched) {
          // Se l'elemento sinistro è stato abbinato a qualcos'altro, troviamo quell'abbinamento
          const incorrectMatch = userMatches.find(um => um.left === correctMatch.left);
          
          // Questa coppia corretta non è stata abbinata dall'utente
          exercisePairResults.push({
            leftItem: correctMatch.left,
            rightItem: correctMatch.right,
            isCorrect: false, // Non è stata abbinata, quindi non è corretta
            wasMissed: true,  // Flag per indicare che è stata saltata completamente
            exerciseId: exercise.id,
            topic: exercise.topic,
            // Se è stata abbinata incorrettamente, mostriamo l'abbinamento errato fatto dall'utente
            userMatch: incorrectMatch ? `${incorrectMatch.left} → ${incorrectMatch.right}` : "Nessuna associazione",
            // Aggiungiamo l'associazione corretta che l'utente avrebbe dovuto fare
            correctMatch: `${correctMatch.left} → ${correctMatch.right}`
          });
        }
      });
      
      // 3. Ora controlliamo se ci sono elementi sinistri che sono stati abbinati a elementi destri errati
      userMatches.forEach(userMatch => {
        const isCorrect = correctMatches.some(
          correctMatch => 
          correctMatch.left === userMatch.left && 
          correctMatch.right === userMatch.right
        );
        
        // Se l'utente ha fatto un abbinamento errato
        if (!isCorrect) {
          // Troviamo l'abbinamento corretto per questo elemento sinistro
          const correctPair = correctMatches.find(cm => cm.left === userMatch.left);
          
          // Verifichiamo se abbiamo già aggiunto questa coppia ai risultati
          const alreadyAdded = exercisePairResults.some(
            epr => epr.leftItem === userMatch.left && epr.rightItem === userMatch.right
          );
          
          // Se non l'abbiamo già aggiunta e c'è un abbinamento corretto, aggiungiamolo
          if (!alreadyAdded && correctPair) {
            // Assicuriamoci che l'associazione corretta per questo elemento non sia già presente
            const correctAssociationPresent = exercisePairResults.some(
              epr => epr.leftItem === correctPair.left && epr.rightItem === correctPair.right && !epr.wasMissed
            );
            
            if (!correctAssociationPresent) {
              exercisePairResults.push({
                leftItem: userMatch.left,
                rightItem: userMatch.right,
                isCorrect: false,
                exerciseId: exercise.id,
                topic: exercise.topic,
                userMatch: `${userMatch.left} → ${userMatch.right}`,
                correctMatch: `${correctPair.left} → ${correctPair.right}`
              });
            }
          }
        }
      });
      
      // Calcola lo score per questo esercizio
      const exerciseScore = exercisePossible > 0 
        ? Math.round((exerciseCorrect / exercisePossible) * 100) 
        : 0;
      
      totalCorrect += exerciseCorrect;
      totalPossible += exercisePossible;
      
      // Aggiungi i risultati di questo esercizio all'array complessivo
      allPairResults = [...allPairResults, ...exercisePairResults];
      
      exerciseResults.push({
        id: exercise.id,
        topic: exercise.topic,
        userMatches: userMatches,
        correctMatches: correctMatches,
        score: exerciseScore,
        correctCount: exerciseCorrect,
        totalCount: exercisePossible,
        pairResults: exercisePairResults
      });
    });
    
    // Calcola lo score totale
    totalScore = totalPossible > 0 
      ? Math.round((totalCorrect / totalPossible) * 100) 
      : 0;
      
    // Log di debug per verificare i dati
    console.log('MatchPairsGame - Risultati calcolati:');
    console.log('  totalScore:', totalScore);
    console.log('  totalCorrect:', totalCorrect);
    console.log('  totalPossible:', totalPossible);
    console.log('  allPairResults:', allPairResults);
    
    return {
      type: 'matchpairs',
      chatId: gameData.chatId,
      score: totalScore,
      correctMatches: totalCorrect,
      totalMatches: totalPossible,
      results: exerciseResults,
      pairResults: allPairResults,
      completed: true,
      completedAt: new Date().toISOString(),
      details: {
        gameType: 'matchpairs',
        totalCorrect: totalCorrect,
        totalPossible: totalPossible,
        percentage: totalScore,
        completed: true,
        completedAt: new Date().toISOString(),
        // Aggiungiamo più dettagli sulla distribuzione degli errori
        missedPairs: allPairResults.filter(pr => pr.wasMissed).length,
        incorrectPairs: allPairResults.filter(pr => !pr.isCorrect && !pr.wasMissed).length
      }
    };
  };
  
  // Gestisci la consegna
  const handleSubmit = () => {
    // Se è l'ultimo gioco e l'utente sta consegnando, non mostriamo i risultati
    if (isLastGame) {
      handleSubmitFinal();
      return;
    }
    
    // Altrimenti procediamo normalmente
    if (!exercises || exercises.length === 0) {
      return;
    }
    
    // Calcola il punteggio
    const result = calculateResults();
    
    // Pulisci sempre il localStorage indipendentemente da eventuali errori
    const lsKey = getLocalStorageKey();
    if (lsKey) {
      try {
        localStorage.removeItem(lsKey);
        console.log('MatchPairsGame: Dati rimossi da localStorage durante handleSubmit', lsKey);
      } catch (e) {
        console.error('MatchPairsGame: Errore nella rimozione dei dati da localStorage:', e);
      }
      
      // Aggiungiamo una pulizia di backup con un delay per maggiore sicurezza
      setTimeout(() => {
        try {
          localStorage.removeItem(lsKey);
          console.log('MatchPairsGame: Tentativo di pulizia ritardata', lsKey);
        } catch (e) {
          console.error('MatchPairsGame: Errore nella pulizia ritardata:', e);
        }
      }, 1000);
    }
    
    if (onComplete) {
      onComplete(result);
    }
  };
  
  // Verifica se tutti gli elementi della colonna sinistra sono stati abbinati
  const areAllItemsMatched = () => {
    if (!currentExercise || !currentExercise.pairs) return false;
    
    // Conta quanti elementi della colonna sinistra sono stati abbinati
    const leftItemsCount = currentExercise.pairs.length;
    const matchedLeftItems = new Set(userMatches.map(match => match.leftId)).size;
    
    return matchedLeftItems === leftItemsCount;
  };
  
  // Controlla se un elemento è abbinato
  const isItemMatched = (itemId) => {
    return userMatches.some(match => match.leftId === itemId || match.rightId === itemId);
  };
  
  // Trova con quale elemento è abbinato
  const getMatchedWithItem = (itemId) => {
    const match = userMatches.find(match => match.leftId === itemId || match.rightId === itemId);
    if (!match) return null;
    
    return match.leftId === itemId ? match.rightId : match.leftId;
  };
  
  if (!exercises || exercises.length === 0) {
    return (
      <div className="matchpairs-game">
        <div className="matchpairs-message">
          <h2>Nessun esercizio disponibile</h2>
          <p>Questo esercizio non contiene domande.</p>
        </div>
      </div>
    );
  }

  if (!currentExercise) {
    return (
      <div className="matchpairs-game">
        <div className="matchpairs-message">
          <h2>Errore nel caricamento dell'esercizio</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="matchpairs-game">
      {totalGames > 1 && (
        <div className="matchpairs-progress-container">
          <div className="matchpairs-game-progress">
            <div className="matchpairs-game-progress-text">
              Esercizio {currentGameIndex + 1} di {totalGames}
            </div>
            <div className="matchpairs-game-progress-bar">
              <div 
                className="matchpairs-game-progress-fill" 
                style={{ width: `${((currentGameIndex + 1) / totalGames) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="matchpairs-question">
        <ProgressCounter current={currentExerciseIndex + 1} total={exercises.length} />
        
        <h3 className="matchpairs-question-title">{currentExercise.topic}</h3>
        
        <div className="matchpairs-container">
          <div className="matchpairs-column left-column">
            {pairs.filter(item => item.type === 'left').map(item => {
              const matchedWithId = getMatchedWithItem(item.id);
              const matchedContent = matchedWithId ? getItemContentById(matchedWithId) : '';
              const isMatched = isItemMatched(item.id);
              
              return (
                <div 
                  key={item.id}
                  className={`matchpairs-item left-item ${selectedLeft?.id === item.id ? 'selected' : ''} ${isMatched ? 'matched' : ''}`}
                  onClick={() => isMatched ? handleRemoveMatch(item.id) : handleItemClick(item)}
                >
                  <div className="matchpairs-item-content">
                    <span className="item-text">{item.content}</span>
                    {isMatched && (
                      <div className="match-indicator">
                        <span className="match-arrow">→</span>
                        <span className="matched-with">{matchedContent}</span>
                        <button 
                          className="remove-match-button"
                          onClick={(e) => handleRemoveMatch(item.id, e)}
                          aria-label="Rimuovi abbinamento"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="matchpairs-column right-column">
            {pairs.filter(item => item.type === 'right').map(item => {
              const isMatched = isItemMatched(item.id);
              
              return (
                <div 
                  key={item.id}
                  className={`matchpairs-item right-item ${isMatched ? 'matched' : ''}`}
                  onClick={() => isMatched ? handleRemoveMatch(item.id) : handleItemClick(item)}
                >
                  <div className="matchpairs-item-content">
                    <span className="item-text">{item.content}</span>
                    {isMatched && (
                      <button 
                        className="remove-match-button right"
                        onClick={(e) => handleRemoveMatch(item.id, e)}
                        aria-label="Rimuovi abbinamento"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <NavigationButtons
        onNext={handleNextExercise}
        onPrev={handlePrevExercise}
        prevDisabled={currentExerciseIndex === 0}
        nextDisabled={!areAllItemsMatched()}
        nextText={currentExerciseIndex < exercises.length - 1 ? 'Avanti' : (isLastGame ? 'Consegna' : 'Completa')}
      />
    </div>
  );
};

export default MatchPairsGame;
